/**
 * MH-39 Quản trị đa tỉnh (đợt 3 phần C4, yêu cầu nghiệp vụ mục 6; mở rộng theo hợp
 * đồng A0 đa tỉnh mục 4 — thêm tỉnh mới).
 *
 * Chỉ quản trị viên nền tảng (`platform.manage`) mở được — `BaoVeTuyen` đã chặn ở
 * tuyến; người dùng thường gọi thẳng đường dẫn nhận trang không có quyền. Nút
 * "Thêm tỉnh mới" vẫn kiểm `platform.manage` lần nữa tại chỗ.
 *
 * Thêm tỉnh: biểu mẫu → kiểm tra phía máy khách → hộp xác nhận → POST
 * /platform/tenants → hiện mật khẩu ban đầu MỘT LẦN kèm hướng dẫn bước tiếp. Máy
 * chủ vẫn là nơi chặn cuối (INVALID_VALUE, DUPLICATE).
 *
 * Chuyển ngữ cảnh sang một tỉnh cấp lại access token phạm vi tỉnh đó và TẢI LẠI
 * TRANG (thay vì điều hướng bằng React Router) để toàn bộ trạng thái ứng dụng
 * (bao gồm hồ sơ ở `NhaCungCapPhien`) nạp lại đúng theo token mới — tránh còn sót
 * dữ liệu tỉnh cũ trong bộ nhớ của trang.
 */
import { useId, useState, type FormEvent, type ReactNode } from 'react';
import { dienVao, vi } from '../ngon-ngu/vi';
import { daTinhApi, type DongTenant, type KetQuaTaoTinh, type ThanTaoTinh } from '../api/da-tinh-api';
import { LoiApi } from '../api/goi-chung';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { dungPhien } from '../trang-thai/ngu-canh-phien';
import { BangDuLieu, SO_DONG_MAC_DINH, type CotBang } from '../thanh-phan/BangDuLieu';
import { HopXacNhan } from '../thanh-phan/HopXacNhan';
import { HopMatKhauMotLan } from './MH34NguoiDung';
import './quan-tri.css';

const tt = vi.daTinh.themTinh;

/** Lớp API dùng ở màn hình — ghi đè được trong kiểm thử. */
export type ApiMH39 = Pick<typeof daTinhApi, 'danhSach' | 'datCongTacDongBo' | 'chuyenNguCanh' | 'taoTinh'>;

/** Giá trị thô của biểu mẫu thêm tỉnh. */
export interface GiaTriThemTinh {
  maTinh: string;
  tenTinh: string;
  emailCongVu: string;
  tenDangNhap: string;
  hoTen: string;
  email: string;
}

const RONG: GiaTriThemTinh = {
  maTinh: '',
  tenTinh: '',
  emailCongVu: '',
  tenDangNhap: '',
  hoTen: '',
  email: '',
};

const MAU_MA_TINH = /^H\d{2}$/;
const MAU_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Kiểm tra phía máy khách; trả câu lỗi đầu tiên hoặc `null` khi hợp lệ. */
export function kiemTraThemTinh(g: GiaTriThemTinh): string | null {
  if (!MAU_MA_TINH.test(g.maTinh.trim().toUpperCase())) return tt.loiMaTinh;
  if (!g.tenTinh.trim()) return tt.loiTenTinh;
  if (g.emailCongVu.trim() && !MAU_EMAIL.test(g.emailCongVu.trim())) return tt.loiEmail;
  if (!g.tenDangNhap.trim()) return tt.loiTenDangNhap;
  if (/\s/.test(g.tenDangNhap.trim())) return tt.loiTenDangNhapKyTu;
  if (!g.hoTen.trim()) return tt.loiHoTen;
  if (g.email.trim() && !MAU_EMAIL.test(g.email.trim())) return tt.loiEmail;
  return null;
}

/** Dựng thân gửi máy chủ: chuẩn hoá mã tỉnh viết hoa, bỏ trường tuỳ chọn để trống. */
export function thanTaoTinh(g: GiaTriThemTinh): ThanTaoTinh {
  const emailCongVu = g.emailCongVu.trim();
  const email = g.email.trim();
  return {
    maTinh: g.maTinh.trim().toUpperCase(),
    tenTinh: g.tenTinh.trim(),
    ...(emailCongVu ? { emailCongVu } : {}),
    quanTri: {
      tenDangNhap: g.tenDangNhap.trim(),
      hoTen: g.hoTen.trim(),
      ...(email ? { email } : {}),
    },
  };
}

/** Diễn giải lỗi tạo tỉnh, kèm chi tiết máy chủ (nếu khác câu diễn giải). */
function moTaLoiTaoTinh(err: unknown): string {
  if (!(err instanceof LoiApi)) return tt.loi.macDinh;
  const goc = (tt.loi as Record<string, string>)[err.ma] ?? tt.loi.macDinh;
  return err.message && err.message !== goc
    ? `${goc} ${dienVao(tt.chiTietLoi, { chiTiet: err.message })}`
    : goc;
}

interface BieuMauThemTinhProps {
  api: Pick<ApiMH39, 'taoTinh'>;
  onHuy: () => void;
  onXong: (kq: KetQuaTaoTinh, tenTinh: string) => void;
}

/** Biểu mẫu thêm tỉnh, gồm cả bước xác nhận trước khi gửi. */
function BieuMauThemTinh({ api, onHuy, onXong }: BieuMauThemTinhProps): ReactNode {
  const [g, datG] = useState<GiaTriThemTinh>(RONG);
  const [loi, datLoi] = useState<string | null>(null);
  const [xacNhan, datXacNhan] = useState(false);
  const [dangGui, datDangGui] = useState(false);
  const idTieuDe = useId();

  const doi =
    (k: keyof GiaTriThemTinh) =>
    (e: { target: { value: string } }): void =>
      datG((cu) => ({ ...cu, [k]: e.target.value }));

  const tiepTuc = (e: FormEvent): void => {
    e.preventDefault();
    const l = kiemTraThemTinh(g);
    datLoi(l);
    if (!l) datXacNhan(true);
  };

  const tao = async (): Promise<void> => {
    const than = thanTaoTinh(g);
    datXacNhan(false);
    datDangGui(true);
    datLoi(null);
    try {
      const kq = await api.taoTinh(than);
      onXong(kq, than.tenTinh);
    } catch (err) {
      datLoi(moTaLoiTaoTinh(err));
    } finally {
      datDangGui(false);
    }
  };

  if (xacNhan) {
    const than = thanTaoTinh(g);
    return (
      <HopXacNhan
        tieuDe={tt.xacNhanTieuDe}
        heQua={dienVao(tt.xacNhanHeQua, {
          tenTinh: than.tenTinh,
          maTinh: than.maTinh,
          tenDangNhap: than.quanTri.tenDangNhap,
        })}
        nhanThucHien={tt.xacNhanNut}
        onHuy={() => datXacNhan(false)}
        onThucHien={() => void tao()}
      />
    );
  }

  const truong = (
    k: keyof GiaTriThemTinh,
    nhan: string,
    tuyChon: { batBuoc?: boolean; moTa?: string; kieu?: string; maxLength?: number } = {},
  ): ReactNode => {
    const id = `them-tinh-${k}`;
    return (
      <div className="truong">
        <label htmlFor={id}>
          {nhan}
          {tuyChon.batBuoc ? <span className="truong__bat-buoc">*</span> : null}
        </label>
        <input
          id={id}
          type={tuyChon.kieu ?? 'text'}
          value={g[k]}
          maxLength={tuyChon.maxLength}
          autoComplete="off"
          onChange={doi(k)}
          aria-describedby={tuyChon.moTa ? `${id}-mo-ta` : undefined}
        />
        {tuyChon.moTa ? (
          <p className="truong__mo-ta" id={`${id}-mo-ta`}>
            {tuyChon.moTa}
          </p>
        ) : null}
      </div>
    );
  };

  return (
    <div className="man-che" role="presentation">
      <form
        className="hop-thoai bieu-mau-nguoi-dung"
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTieuDe}
        data-vung="bieu-mau-them-tinh"
        onSubmit={tiepTuc}
        noValidate
      >
        <h2 id={idTieuDe}>{tt.tieuDe}</h2>
        <fieldset className="truong">
          <legend>{tt.nhomTinh}</legend>
          {truong('maTinh', tt.maTinh, { batBuoc: true, moTa: tt.maTinhMoTa, maxLength: 3 })}
          {truong('tenTinh', tt.tenTinh, { batBuoc: true, maxLength: 200 })}
          {truong('emailCongVu', tt.emailCongVu, { kieu: 'email' })}
        </fieldset>
        <fieldset className="truong">
          <legend>{tt.nhomQuanTri}</legend>
          {truong('tenDangNhap', tt.tenDangNhap, { batBuoc: true, moTa: tt.tenDangNhapMoTa })}
          {truong('hoTen', tt.hoTen, { batBuoc: true })}
          {truong('email', tt.email, { kieu: 'email' })}
        </fieldset>
        {loi ? (
          <p className="dai-thong-bao dai-thong-bao--loi" role="alert">
            {loi}
          </p>
        ) : null}
        <div className="hop-thoai__nut">
          <button type="button" className="nut" onClick={onHuy} disabled={dangGui}>
            {tt.huy}
          </button>
          <button type="submit" className="nut nut--chinh" disabled={dangGui}>
            {dangGui ? tt.dangTao : tt.tiepTuc}
          </button>
        </div>
      </form>
    </div>
  );
}

/** Hướng dẫn bước tiếp sau khi tạo tỉnh — chèn vào hộp mật khẩu một lần. */
function HuongDanBuocTiep({ tenDangNhap }: { tenDangNhap: string }): ReactNode {
  return (
    <section data-vung="huong-dan-buoc-tiep">
      <p>
        {tt.tenDangNhap}: <strong>{tenDangNhap}</strong>
      </p>
      <h3>{tt.buocTiepTieuDe}</h3>
      <ol>
        {tt.buocTiep.map((b) => (
          <li key={b}>{b}</li>
        ))}
      </ol>
    </section>
  );
}

export interface MH39Props {
  api?: ApiMH39;
}

export function MH39DaTinh({ api = daTinhApi }: MH39Props = {}): ReactNode {
  const phien = dungPhien();
  const duocThemTinh = phien.coQuyen('platform.manage');
  const [moc, datMoc] = useState(0);
  const [dangChuyen, datDangChuyen] = useState<string | null>(null);
  const [loi, datLoi] = useState<string | null>(null);
  const [moBieuMau, datMoBieuMau] = useState(false);
  const [vuaTao, datVuaTao] = useState<{ kq: KetQuaTaoTinh; tenTinh: string } | null>(null);

  const kq = dungTaiDuLieu<DongTenant[]>({
    tai: (signal) => api.danhSach(signal),
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [moc],
  });
  const taiLai = (): void => datMoc((x) => x + 1);

  const dongBo = async (t: DongTenant): Promise<void> => {
    datLoi(null);
    try {
      await api.datCongTacDongBo(t.id, !t.congTacGuiDuLieu);
      taiLai();
    } catch (err) {
      datLoi(err instanceof LoiApi ? err.message : 'Thao tác thất bại');
    }
  };

  const chuyenNguCanh = async (t: DongTenant): Promise<void> => {
    if (!window.confirm(vi.daTinh.xacNhanChuyen)) return;
    datDangChuyen(t.id);
    datLoi(null);
    try {
      await api.chuyenNguCanh(t.id);
      // Tải lại trang để mọi thành phần (ngữ cảnh phiên, điều hướng) nạp lại theo
      // token mới — đúng chủ đích "không thao tác nhầm tỉnh" của yêu cầu nghiệp vụ.
      window.location.href = '/tong-quan';
    } catch (err) {
      datLoi(err instanceof LoiApi ? err.message : 'Không chuyển được ngữ cảnh');
      datDangChuyen(null);
    }
  };

  // Cột "Số đơn vị hành chính" chỉ hiện khi API trả trường này (API cũ không có).
  const coSoDonVi = (kq.duLieu ?? []).some((d) => typeof d.soDonViHanhChinh === 'number');

  const cot: CotBang<DongTenant>[] = [
    { khoa: 'tenTinh', nhan: vi.daTinh.cot.tenTinh, ve: (d) => `${d.tenTinh} (${d.maTinh})` },
    { khoa: 'soThietBi', nhan: vi.daTinh.cot.soThietBi, canPhai: true, ve: (d) => d.soThietBi },
    { khoa: 'soNguoiDung', nhan: vi.daTinh.cot.soNguoiDung, canPhai: true, ve: (d) => d.soNguoiDung },
    ...(coSoDonVi
      ? [
          {
            khoa: 'soDonViHanhChinh',
            nhan: vi.daTinh.cot.soDonViHanhChinh,
            canPhai: true,
            ve: (d: DongTenant) => d.soDonViHanhChinh ?? 0,
          },
        ]
      : []),
    {
      khoa: 'dungLuong',
      nhan: vi.daTinh.cot.dungLuong,
      canPhai: true,
      ve: (d) =>
        `${(d.dungLuongDaDungByte / 1024 / 1024 / 1024).toFixed(2)} GB${
          d.phanTramDungLuong !== null ? ` (${d.phanTramDungLuong}%)` : ''
        }`,
    },
    {
      khoa: 'dongBo',
      nhan: vi.daTinh.cot.dongBo,
      ve: (d) => (
        <label>
          <input type="checkbox" checked={d.congTacGuiDuLieu} onChange={() => void dongBo(d)} />
        </label>
      ),
    },
    {
      khoa: 'hanhDong',
      nhan: vi.daTinh.cot.hanhDong,
      ve: (d) => (
        <button
          type="button"
          className="nut nut--nho"
          disabled={dangChuyen === d.id}
          onClick={() => void chuyenNguCanh(d)}
        >
          {dangChuyen === d.id ? vi.daTinh.dangChuyen : vi.daTinh.chuyenNguCanh}
        </button>
      ),
    },
  ];

  return (
    <div className="man-hinh man-hinh--da-tinh">
      <header className="man-hinh__dau">
        <h1>{vi.daTinh.tieuDe}</h1>
        {duocThemTinh ? (
          <div className="man-hinh__hanh-dong">
            <button type="button" className="nut nut--chinh" onClick={() => datMoBieuMau(true)}>
              {tt.nut}
            </button>
          </div>
        ) : null}
      </header>
      {loi ? <p role="alert">{loi}</p> : null}

      <BangDuLieu<DongTenant>
        nhan={vi.daTinh.tieuDe}
        cot={cot}
        dong={kq.duLieu ?? []}
        khoaDong={(d) => d.id}
        trangThai={kq.trangThai}
        loi={kq.loi}
        taiLau={kq.taiLau}
        onThuLai={taiLai}
        tong={kq.duLieu?.length ?? 0}
        trang={1}
        soDongMoiTrang={SO_DONG_MAC_DINH}
        onDoiTrang={() => undefined}
        onDoiSoDong={() => undefined}
        dangLoc={false}
        onXoaLoc={() => undefined}
        moTaRong={vi.daTinh.rongMoTa}
      />

      {moBieuMau && duocThemTinh ? (
        <BieuMauThemTinh
          api={api}
          onHuy={() => datMoBieuMau(false)}
          onXong={(kqTao, tenTinh) => {
            datMoBieuMau(false);
            datVuaTao({ kq: kqTao, tenTinh });
            taiLai();
          }}
        />
      ) : null}

      {vuaTao ? (
        <HopMatKhauMotLan
          tieuDe={dienVao(tt.daTaoTieuDe, {
            tenTinh: vuaTao.tenTinh,
            maTinh: vuaTao.kq.maTinh,
            tenDangNhap: vuaTao.kq.tenDangNhap,
          })}
          matKhau={vuaTao.kq.matKhauBanDau}
          onDong={() => datVuaTao(null)}
          phuLuc={<HuongDanBuocTiep tenDangNhap={vuaTao.kq.tenDangNhap} />}
        />
      ) : null}
    </div>
  );
}
